import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "Granola", url: "https://www.granola.ai" };

const en: ComparePageCopy = {
  seo: {
    title: "Granola vs. Minuta — notes after the call vs. copilot during the call",
    description:
      "Granola writes great notes after your meeting. Minuta helps you survive the meeting in real time — live copilot answers in English, Portuguese, and Spanish, no bot, macOS and Windows, €9/mo.",
    keywords:
      "granola alternative, granola vs minuta, real-time meeting copilot, granola portuguese, cross-platform meeting transcription mac windows, ai meeting notes mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "Granola vs. Minuta: beautiful notes vs. live answers",
  intro:
    "Choose Granola when you want polished notes and a durable meeting memory after the call. Choose Minuta when the risk is freezing on a fast question while the call is still happening.",
  honesty: {
    heading: "Be honest about the job",
    pickThem: {
      name: "Granola",
      bullets: [
        "You want beautiful post-meeting notes, summaries, and a searchable meeting memory.",
        "You're fluent in the language of the call and do not need help forming an answer live.",
        "Your stack is Affinity / HubSpot / Notion / Slack / Attio and the integrations matter.",
        "You're comfortable with Granola's privacy model: desktop-local transcription, with notes stored in a US AWS VPC.",
      ],
    },
    pickMinuta: [
      "The hard moment is during the call: a fast question lands and you need words now.",
      "You're a non-native or second-language speaker who wants live transcript, translation, and a draft answer before the pause gets awkward.",
      "You want the assistant to stay invisible: no bot joins and no extra participant appears.",
      "You want secure transient cloud processing and local file storage, with no permanent cloud logs.",
      "You still want notes after the call, but the buying reason is live answers, not meeting memory.",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "Granola", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot in the meeting",
        them: {
          value: 'No — "transcribes your computer\'s audio directly, no meeting bots"',
          cite: { url: "https://www.granola.ai/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No — same posture" },
      },
      {
        feature: "Real-time answer copilot",
        them: { value: "No — notes generated after the call" },
        minuta: { value: "Yes (Pro) — drafts answers as questions land" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Yes — real-time" },
      },
      {
        feature: "Audio processing",
        them: {
          value: "Local capture (transient cloud API)",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Where notes live",
        them: {
          value: "US AWS VPC",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "On your computer (transient processing)" },
      },
      {
        feature: "Native PT website",
        them: {
          value: "No — English-only site",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Yes" },
      },
      {
        feature: "Native ES website",
        them: { value: "No — English-only site" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + GDPR; DPA on request; no HIPAA",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacy-first by design (DPA on roadmap)" },
      },
      {
        feature: "Platform",
        them: {
          value: "Mac (Apple Silicon native); Windows added later",
          cite: {
            url: "https://www.itsconvo.com/blog/best-ai-meeting-assistants-mac-2026",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "macOS + Windows" },
      },
      {
        feature: "Free tier",
        them: {
          value: "Free Basic",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid",
        them: {
          value: "Business $14/u/mo · Enterprise $35/u/mo",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-04-30; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins: the live-answer moment",
    bullets: [
      "Granola is strongest around the meeting: clean notes, memory, and follow-up. Minuta is built for the minute someone asks you a question in English, Portuguese, or Spanish and you need to answer during the call.",
      "Live transcript and translation reduce the panic window: you can see what was asked, keep up with accents and speed, then ask Minuta for a draft response.",
      "The answer copilot is the core difference. Granola helps you remember what happened; Minuta helps you decide what to say next.",
      "Privacy posture matters in sensitive calls: no bot joins, and Minuta uses secure transient APIs that do not store your recordings or transcript logs in the cloud.",
      "Use Granola if the problem is documentation quality. Use Minuta if the risk is silence on a high-stakes live call.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Try Minuta on your next difficult live call",
    body: "Run the 7-day trial on a real call where freezing would cost you. No card. No meeting bot.",
    primary: "Start the 7-day trial",
    secondary: "Download for macOS / Windows",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "Granola vs. Minuta — preço no Brasil e resposta ao vivo",
    description:
      "Comparação honesta entre Granola e Minuta para brasileiros: preço em BRL, notas depois da call, resposta ao vivo em inglês e suporte PT-BR.",
    keywords:
      "alternativa granola, granola vs minuta, preço granola no brasil, copiloto de reunião em tempo real, granola português, ia para reunião em inglês",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "Granola vs. Minuta: notas bonitas vs. respostas ao vivo",
  intro:
    "Escolha Granola quando você quer notas bonitas e uma memória da reunião depois da call. Escolha Minuta quando o risco é travar numa pergunta rápida em inglês enquanto a call ainda está acontecendo — e quando pagar em Real importa.",
  honesty: {
    heading: "Seja honesto sobre o trabalho",
    pickThem: {
      name: "Granola",
      bullets: [
        "Você quer notas bonitas depois da reunião, resumos e uma memória pesquisável das calls.",
        "Você é fluente no idioma da call e não precisa de ajuda para formular uma resposta ao vivo.",
        "Seu stack é Affinity / HubSpot / Notion / Slack / Attio e as integrações importam.",
        "Você aceita pagar o plano Business em USD e lidar com câmbio, IOF e cobrança internacional.",
        "Você está confortável com o modelo de privacidade do Granola: transcrição local no desktop, com notas guardadas numa VPC AWS dos EUA.",
      ],
    },
    pickMinuta: [
      "O momento difícil acontece durante a call: cai uma pergunta rápida em inglês e você precisa achar as palavras agora.",
      "Você fala inglês como segunda língua e quer transcript ao vivo, tradução e uma sugestão de resposta antes do silêncio ficar estranho.",
      "Você quer um assistente invisível: nenhum bot entra e nenhum participante extra aparece.",
      "Você quer pagar em Real: Founding 100 por R$22.50/mês nas primeiras 100 vagas, travado por 12 meses.",
      "Você quer processamento temporário seguro e arquivos salvos no seu computador, sem logs na nuvem.",
      "Você ainda quer notas depois da call, mas o motivo de compra é resposta ao vivo, não memória da reunião.",
    ],
  },
  table: {
    heading: "Preço e decisão lado a lado",
    headers: { feature: "Recurso", them: "Granola", minuta: "Minuta" },
    rows: [
      {
        feature: "Preço no Brasil",
        them: {
          value: "Business: US$14/usuário/mês (aprox. R$70, antes de IOF)",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-05-21" },
        },
        minuta: { value: "Founding 100: R$22.50/mês por 12 meses; depois Pro R$35/mês" },
      },
      {
        feature: "Cobra em Real",
        them: { value: "Não — cobrança em USD" },
        minuta: { value: "Sim — Stripe BR em BRL" },
      },
      {
        feature: "Bot na reunião",
        them: {
          value: 'Não — "transcreve o áudio do seu computador diretamente, sem bot"',
          cite: { url: "https://www.granola.ai/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Não — mesma postura" },
      },
      {
        feature: "Copiloto sugerindo resposta em tempo real",
        them: { value: "Não — ata gerada depois da call" },
        minuta: { value: "Sim (Pro) — sugere resposta no momento da pergunta" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: { value: "Não" },
        minuta: { value: "Sim — tempo real" },
      },
      {
        feature: "Processamento do áudio",
        them: {
          value: "Captura local (API temporária na nuvem)",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Onde a ata fica",
        them: {
          value: "VPC AWS nos EUA",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No seu computador (processamento temporário)" },
      },
      {
        feature: "Site nativo em PT",
        them: {
          value: "Não — só em inglês",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sim" },
      },
      {
        feature: "Site nativo em ES",
        them: { value: "Não — só em inglês" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + GDPR; DPA sob pedido; sem HIPAA",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidade por design (DPA no roadmap)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac (nativo Apple Silicon); Windows depois",
          cite: {
            url: "https://www.itsconvo.com/blog/best-ai-meeting-assistants-mac-2026",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "macOS + Windows" },
      },
      {
        feature: "Plano grátis",
        them: {
          value: "Free Basic",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago",
        them: {
          value: "Business US$ 14/u/mês · Enterprise US$ 35/u/mês",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-05-21" },
        },
        minuta: { value: "Pro R$35/mês; Founding 100 R$22.50/mês por 12 meses" },
      },
    ],
    footnote: "Preços dos concorrentes em USD consultados em 2026-05-21; conversão BRL aproximada, antes de IOF e variação cambial.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha: a hora da resposta ao vivo",
    bullets: [
      "Granola é mais forte ao redor da reunião: notas limpas, memória e follow-up. Minuta foi feito para o minuto em que alguém te pergunta algo em inglês, português ou espanhol e você precisa responder durante a call.",
      "Transcript e tradução ao vivo reduzem a janela de pânico: você vê o que foi perguntado, acompanha sotaque e velocidade, e pede uma resposta sugerida ao Minuta.",
      "O copiloto de resposta é a diferença central. Granola ajuda você a lembrar o que aconteceu; Minuta ajuda você a decidir o que dizer agora.",
      "A postura de privacidade importa em calls sensíveis: nenhum bot entra, e o Minuta usa APIs transientes seguras que não armazenam suas gravações ou logs de transcrição na nuvem.",
      "Use Granola se o problema é qualidade de documentação. Use Minuta se o risco é ficar em silêncio numa call importante ao vivo.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Teste o Minuta na sua próxima call difícil ao vivo",
    body: "Use o trial de 7 dias numa call real em que travar custaria caro. Sem cartão. Sem bot na call.",
    primary: "Começar trial de 7 dias",
    secondary: "Baixar para macOS / Windows",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "Granola vs. Minuta — notas después de la call vs. copiloto durante la call",
    description:
      "Granola escribe un acta excelente después de la reunión. Minuta te ayuda a sobrevivir la reunión en tiempo real — en inglés, español y portugués, sin bot y con retención cero.",
    keywords:
      "alternativa granola, granola vs minuta, copiloto de reunión en tiempo real, granola español, transcripción de reunión mac, acta de reunión con ia mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "Granola vs. Minuta: notas bonitas vs. respuestas en vivo",
  intro:
    "Elige Granola cuando quieres notas bonitas y una memoria de la reunión después de la call. Elige Minuta cuando el riesgo es quedarte bloqueado ante una pregunta rápida mientras la call sigue en vivo.",
  honesty: {
    heading: "Sé honesto sobre el trabajo",
    pickThem: {
      name: "Granola",
      bullets: [
        "Quieres notas bonitas después de la reunión, resúmenes y una memoria buscable de tus calls.",
        "Eres fluido en el idioma de la call y no necesitas ayuda para formular una respuesta en vivo.",
        "Tu stack es Affinity / HubSpot / Notion / Slack / Attio y las integraciones importan.",
        "Estás cómodo con el modelo de privacidad de Granola: transcripción local en desktop, con notas guardadas en una VPC AWS en EE.UU.",
      ],
    },
    pickMinuta: [
      "El momento difícil pasa durante la call: llega una pregunta rápida y necesitas encontrar las palabras ahora.",
      "Hablas inglés como segunda lengua y quieres transcript en vivo, traducción y una sugerencia de respuesta antes de que el silencio pese.",
      "Quieres un asistente invisible: no entra ningún bot y no aparece ningún participante extra.",
      "Quieres procesamiento temporal seguro y archivos guardados en tu computadora, sin registros en la nube.",
      "Sigues queriendo notas después de la call, pero la razón de compra es respuesta en vivo, no memoria de reunión.",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "Granola", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot en la reunión",
        them: {
          value: 'No — "transcribe el audio del ordenador directamente, sin bots"',
          cite: { url: "https://www.granola.ai/", capturedAt: "2026-04-30" },
        },
        minuta: { value: "No — misma postura" },
      },
      {
        feature: "Copiloto sugiriendo respuesta en tiempo real",
        them: { value: "No — acta generada post-call" },
        minuta: { value: "Sí (Pro) — sugiere respuesta en el momento" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: { value: "No" },
        minuta: { value: "Sí — tiempo real" },
      },
      {
        feature: "Procesamiento del audio",
        them: {
          value: "Captura local (API transitoria en la nube)",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Dónde vive el acta",
        them: {
          value: "VPC AWS en EE.UU.",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "En tu computadora (procesamiento transitorio)" },
      },
      {
        feature: "Web nativa en PT",
        them: {
          value: "No — solo en inglés",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Sí" },
      },
      {
        feature: "Web nativa en ES",
        them: { value: "No — solo en inglés" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Compliance",
        them: {
          value: "SOC 2 II + GDPR; DPA bajo petición; sin HIPAA",
          cite: { url: "https://www.granola.ai/security", capturedAt: "2026-04-30" },
        },
        minuta: { value: "Privacidad por diseño (DPA en roadmap)" },
      },
      {
        feature: "Plataforma",
        them: {
          value: "Mac (nativo Apple Silicon); Windows después",
          cite: {
            url: "https://www.itsconvo.com/blog/best-ai-meeting-assistants-mac-2026",
            capturedAt: "2026-04-30",
          },
        },
        minuta: { value: "macOS + Windows" },
      },
      {
        feature: "Plan gratis",
        them: {
          value: "Free Basic",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago",
        them: {
          value: "Business US$ 14/u/mes · Enterprise US$ 35/u/mes",
          cite: { url: "https://www.granola.ai/pricing", capturedAt: "2026-04-30" },
        },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-04-30; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta: el momento de responder en vivo",
    bullets: [
      "Granola es más fuerte alrededor de la reunión: notas limpias, memoria y follow-up. Minuta está hecho para el minuto en que alguien te pregunta algo en inglés, portugués o español y necesitas responder durante la call.",
      "Transcript y traducción en vivo reducen la ventana de pánico: ves qué te preguntaron, sigues acentos y velocidad, y le pides a Minuta una respuesta sugerida.",
      "El copiloto de respuesta es la diferencia central. Granola te ayuda a recordar lo que pasó; Minuta te ayuda a decidir qué decir ahora.",
      "La postura de privacidad importa en calls sensibles: no entra ningún bot, y Minuta usa APIs transitorias seguras que no almacenan tus grabaciones ni registros de transcripción en la nube.",
      "Usa Granola si el problema es la calidad de documentación. Usa Minuta si el riesgo es quedarte en silencio en una call importante en vivo.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Prueba Minuta en tu próxima call difícil en vivo",
    body: "Usa la prueba de 7 días en una call real donde bloquearte tendría coste. Sin tarjeta. Sin bot en la call.",
    primary: "Empezar prueba de 7 días",
    secondary: "Descargar para macOS / Windows",
  },
};

export const compareGranolaCopy: ComparePageCopyByLang = { en, pt, es };
